import { cn } from "@gigpro/ui/tailwind/cn";
import { Link } from "@tanstack/react-router";
import { cva } from "class-variance-authority";
import type { NavItemButton, NavItemLink } from "layouts/BaseLayout/types";
import { useNavLinkProps, useDoesPathMatch } from "layouts/BaseLayout/utils";
import type React from "react";

type DesktopNavItemProps = {
  item: NavItemLink | NavItemButton;
  onClick?: () => void;
  hideText?: boolean;
  className?: string;
};

const iconWrapperClassNames = cn("[&>*:first-child]:mr-2 [&>*:first-child]:size-4");
const commonItemClassNames = cn(
  "flex flex-row items-center border-y-[3px] border-transparent p-2 px-3.5 text-large tracking-tight whitespace-nowrap",
);
const hideTextClassNames = cn(
  "p-1.5 [&>*:first-child]:mr-0 [&>span>*:first-child]:mr-0 [&>span>span>*:first-child]:size-4",
);

const buttonItemVariants = cva(commonItemClassNames, {
  variants: {
    variant: { default: iconWrapperClassNames, pill: "px-2" },
    hideText: { true: "", false: "" },
  },
  compoundVariants: [{ variant: "default", hideText: true, className: hideTextClassNames }],
  defaultVariants: { variant: "default", hideText: false },
});

export const DesktopNavItem: React.FC<DesktopNavItemProps> = ({ item, onClick, hideText, className }) => {
  const matchesPath = useDoesPathMatch(item.type === "link" ? item : { path: "", external: true });
  const navLinkProps = useNavLinkProps();

  if (item.type === "link") {
    const { title, icon, external } = item;

    return (
      <Link
        key={title}
        {...navLinkProps(item)}
        target={external ? "_blank" : "_self"}
        onClick={onClick}
        data-active={matchesPath}
        className={cn(
          commonItemClassNames,
          hideText && hideTextClassNames,
          "transition-all duration-200",
          matchesPath && "border-b-brand-hover",
          className,
        )}
      >
        <span className={iconWrapperClassNames}>{icon}</span>
        {hideText ? null : title}
        {item.isBeta ? <span className="ml-1.5 rounded-xl bg-tags-green px-2 text-small text-black">Beta</span> : null}
      </Link>
    );
  }
  if (item.type === "button") {
    return (
      <button
        key={item.title}
        type="button"
        onClick={() => {
          item.onClick();
          onClick?.();
        }}
        className={cn(buttonItemVariants({ variant: item.variant, hideText }), className)}
      >
        {item.icon}
        {hideText || item.variant === "pill" ? null : item.title}
      </button>
    );
  }
};
